import { useState } from "react";
import { MessageSquareShare } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { ShareSessionBody } from "~/pages/manifold-nextgen/components/conversations/share/ShareSessionBody";
import { useConversationShare } from "~/pages/manifold-nextgen/components/conversations/share/useConversationShare";
import { FeaturedSession } from "~/services/projects";

interface SharePopoverProps {
  conversationId: string;
  projectId: string;
  isOwner: boolean;
  displayName: string;
  agentId?: string | null;
  featuredSessions: FeaturedSession[] | undefined;
}

// Composer-row entry point for sharing the current session, styled to sit
// alongside the Skills/Connectors pickers. Opens the share-link surface inline
// as a popover rather than the centered ShareConversationDialog.
export const SharePopover = ({
  conversationId,
  projectId,
  isOwner,
  displayName,
  agentId,
  featuredSessions,
}: SharePopoverProps) => {
  const [open, setOpen] = useState(false);
  const share = useConversationShare(conversationId, open);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-label="Share"
              className="size-8 px-0 text-muted-foreground"
            >
              <MessageSquareShare className="size-3.5" />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>Share</TooltipContent>
      </Tooltip>
      <PopoverContent
        align="start"
        className="w-96 p-0"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <div className="border-b px-3 py-2">
          <p className="text-sm font-semibold">Share Session</p>
          <p className="text-xs text-muted-foreground">Anyone with the link can view, read-only.</p>
        </div>
        <div className="p-3">
          <ShareSessionBody
            share={share}
            projectId={projectId}
            isOwner={isOwner}
            displayName={displayName}
            agentId={agentId}
            featuredSessions={featuredSessions}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
};
